import { BaseModel, IModelOptions } from 'models/BaseModel';
import { get } from 'services/request';
import { IRefIds } from '../constants/refs';

export interface IUnifiedDashboardCourse {
  id: number;
  name: string;
  languageId: number;
  publishLesson: number;
  startLesson: number;
  endLesson: number;
}

export interface IUnifiedDashboardRef {
  ref: number;
  refName: string;
  isAllLessons: boolean;
}

export interface IUnifiedDashboardCell {
  pending: number;
  doing: number;
  done: number;
}

export interface IUnifiedDashboardResponse {
  courses: IUnifiedDashboardCourse[];
  refs: IUnifiedDashboardRef[];
  data: { [ref: number]: { [courseId: number]: IUnifiedDashboardCell } };
}

interface ICourseDashboardResponse {
  courseId: number;
  startLesson: number;
  endLesson: number;
  data: { [ref: number]: IUnifiedDashboardCell };
}

export interface IUnifiedDialect {
  dialectId: number;
  dialectName: string;
  dialectCode: string;
  languageName: string;
  languageId: number;
}

export interface IUnifiedLanguage {
  id: number;
  name: string;
}

interface IDialectsResponse {
  dialects: IUnifiedDialect[];
  languages: IUnifiedLanguage[];
}

export default class TranslationUnifiedDashboardModel extends BaseModel {
  private dialectId = 0;
  private languageId = 0;
  private dashboardData: IUnifiedDashboardResponse | null = null;
  private dialects: IUnifiedDialect[] = [];
  private languages: IUnifiedLanguage[] = [];
  private loadingCourses: Set<number> = new Set();

  constructor(options: IModelOptions) {
    super(options);
    this.ref = IRefIds.translationUnified;
    this.gridName = 'TranslationUnifiedDashboard';
  }

  getDialects(): IUnifiedDialect[] {
    return this.dialects;
  }

  getLanguages(): IUnifiedLanguage[] {
    return this.languages;
  }

  getDashboardData(): IUnifiedDashboardResponse | null {
    return this.dashboardData;
  }

  getDialectId(): number {
    return this.dialectId;
  }

  getLanguageId(): number {
    return this.languageId;
  }

  setDialectId(dialectId: number) {
    this.dialectId = dialectId;
  }

  setLanguageId(languageId: number) {
    this.languageId = languageId;
  }

  isCourseLoading(courseId: number): boolean {
    return this.loadingCourses.has(courseId);
  }

  async loadDialects() {
    const response = await get<IDialectsResponse>('/translation-unified/dialects');
    this.dialects = response.dialects;
    this.languages = response.languages;
    this.render();
  }

  async getDashboard() {
    if (!this.dialectId || this.languageId === 0) {
      return;
    }
    this.loadingGrid = true;
    this.render();

    const params: Record<string, number> = { dialectId: this.dialectId };
    if (this.languageId > 0) {
      params.languageId = this.languageId;
    }

    const response = await get<IUnifiedDashboardResponse>('/translation-unified/dashboard', params);

    this.dashboardData = response;
    this.loadingGrid = false;
    this.render();
  }

  async reloadCourse(courseId: number, startLesson: number, endLesson: number) {
    if (!this.dialectId || !this.dashboardData) {
      return;
    }

    this.loadingCourses.add(courseId);
    this.render();

    const response = await get<ICourseDashboardResponse>('/translation-unified/dashboard-course', {
      dialectId: this.dialectId,
      courseId,
      startLesson,
      endLesson,
    });

    // Merge into existing data
    if (this.dashboardData) {
      this.dashboardData.courses = this.dashboardData.courses.map((c) =>
        c.id === courseId ? { ...c, startLesson: response.startLesson, endLesson: response.endLesson } : c,
      );

      for (const ref of Object.keys(response.data)) {
        const refNum = parseInt(ref);
        if (!this.dashboardData.data[refNum]) {
          this.dashboardData.data[refNum] = {};
        }
        this.dashboardData.data[refNum][courseId] = response.data[refNum];
      }
    }

    this.loadingCourses.delete(courseId);
    this.render();
  }
}
